import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Box, Typography
} from '@mui/material';
import { SubscriptionBillingService } from '../../services/subscriptionBilling.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { InstitutionService } from '../../services/institution.service';
import { IInstitution } from '../../interfaces/institution.interface';
import { InvoiceKind } from '../../interfaces/billing.interface';

interface IInstitutionWithEmployeeCount extends IInstitution {
    employeeCount?: number;
}

interface Props {
    open: boolean;
    institutions: IInstitutionWithEmployeeCount[];
    onClose: () => void;
    onSuccess: () => void;
}

// The add-on description is invoice content stored by the backend and shown on the invoice, so it stays in
// English whatever the operator's language (like CSV and PDF content).
const ADDON_PREFIX = 'Accounting add-on — ';

/** "Accounting add-on — August 2026" for the month the period starts in (or the current month). */
const addonDescription = (periodStart: string): string => {
    const d = periodStart ? new Date(`${periodStart}T00:00:00`) : new Date();
    return ADDON_PREFIX + d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
};

const InvoiceCreateDialog: React.FC<Props> = ({ open, institutions, onClose, onSuccess }) => {
    const { showSuccess, showApiError } = useToast();
    const { t } = useTranslation('billing');
    const [submitting, setSubmitting] = useState(false);

    const [institutionId, setInstitutionId] = useState('');
    const [periodStart, setPeriodStart] = useState('');
    const [periodEnd, setPeriodEnd] = useState('');
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [kind, setKind] = useState<InvoiceKind>('SUBSCRIPTION');
    // The amount the add-on prefill last wrote; any other value was typed by the operator and is kept.
    const prefilledAmount = useRef<string | null>(null);

    useEffect(() => {
        if (open) {
            setKind('SUBSCRIPTION');
            prefilledAmount.current = null;
            setInstitutionId('');
            setPeriodStart('');
            setPeriodEnd('');
            setAmount('');
            setDescription('');
            setDueDate('');
        }
    }, [open]);

    // The add-on is billed at the institution's own fee, so choosing it fills in amount and description.
    useEffect(() => {
        if (kind !== 'ACCOUNTING_ADDON') return;
        setDescription(prev => (!prev || prev.startsWith(ADDON_PREFIX) ? addonDescription(periodStart) : prev));
        if (!institutionId) return;
        let cancelled = false;
        InstitutionService.getAccountingFee(institutionId)
            .then(fee => {
                if (cancelled || fee == null) return;
                const feeText = String(fee);
                setAmount(prev => {
                    if (prev !== '' && prev !== prefilledAmount.current) return prev;
                    prefilledAmount.current = feeText;
                    return feeText;
                });
            })
            .catch(error => showApiError(error, t('invoiceCreateDialog.feeLoadFailed')));
        return () => { cancelled = true; };
    }, [kind, institutionId, periodStart]); // eslint-disable-line react-hooks/exhaustive-deps

    const changeKind = (next: InvoiceKind) => {
        if (next === 'SUBSCRIPTION') {
            // Drop what the add-on filled in; keep anything the operator typed.
            setAmount(prev => (prev === prefilledAmount.current ? '' : prev));
            setDescription(prev => (prev.startsWith(ADDON_PREFIX) ? '' : prev));
            prefilledAmount.current = null;
        }
        setKind(next);
    };

    const selectedInstitution = institutions.find(inst => inst.id === institutionId);

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            await SubscriptionBillingService.createInvoice({
                institutionId,
                periodStart,
                periodEnd,
                amount: Number(amount),
                description: description || undefined,
                dueDate: dueDate || undefined,
                employeeCount: kind === 'SUBSCRIPTION' ? selectedInstitution?.employeeCount : undefined,
                kind,
            });
            showSuccess(t('invoiceCreateDialog.created'));
            onSuccess();
            onClose();
        } catch (error) {
            showApiError(error, t('invoiceCreateDialog.createFailed'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('invoiceCreateDialog.title')}</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <FormControl fullWidth required>
                        <InputLabel id="invoice-institution-label">{t('invoiceCreateDialog.institution')}</InputLabel>
                        <Select
                            labelId="invoice-institution-label"
                            label={t('invoiceCreateDialog.institution')}
                            value={institutionId}
                            onChange={(e) => setInstitutionId(e.target.value)}
                            inputProps={{ 'aria-label': t('invoiceCreateDialog.institution') }}
                        >
                            {institutions.map(inst => (
                                <MenuItem key={inst.id} value={inst.id}>{inst.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        id="invoice-kind"
                        select
                        label={t('invoiceCreateDialog.invoiceType')}
                        value={kind}
                        onChange={(e) => changeKind(e.target.value as InvoiceKind)}
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    >
                        <option value="SUBSCRIPTION">{t('invoiceCreateDialog.kindSubscription')}</option>
                        <option value="ACCOUNTING_ADDON">{t('invoiceCreateDialog.kindAccountingAddon')}</option>
                    </TextField>

                    {selectedInstitution && (
                        <Typography variant="body2" color="text.secondary">
                            {t('invoiceCreateDialog.employees', { value: selectedInstitution.employeeCount ?? t('invoiceCreateDialog.notAvailable') })}
                        </Typography>
                    )}

                    <TextField
                        label={t('invoiceCreateDialog.periodStart')}
                        type="date"
                        value={periodStart}
                        onChange={(e) => setPeriodStart(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        required
                        fullWidth
                    />

                    <TextField
                        label={t('invoiceCreateDialog.periodEnd')}
                        type="date"
                        value={periodEnd}
                        onChange={(e) => setPeriodEnd(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        required
                        fullWidth
                    />

                    <TextField
                        label={t('invoiceCreateDialog.amount')}
                        type="text"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        inputProps={{ inputMode: 'decimal' }}
                        required
                        fullWidth
                    />

                    <TextField
                        label={t('invoiceCreateDialog.description')}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        rows={2}
                        fullWidth
                    />

                    <TextField
                        label={t('invoiceCreateDialog.dueDate')}
                        type="date"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>{t('invoiceCreateDialog.cancel')}</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitting || !institutionId || !periodStart || !periodEnd || !amount}
                >
                    {submitting ? t('invoiceCreateDialog.creating') : t('invoiceCreateDialog.create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InvoiceCreateDialog;
